import { useAuth } from './useAuth';

interface FeatureAccessState {
  accessibleFeatureKeys: string[];
  loading: boolean;
  hasFeature: (key: string) => boolean;
  hasAccounting: boolean;
}

/**
 * Which add-on features the current user can reach.
 *
 * These arrive with the user at login and on every session rehydration, so there is nothing
 * to fetch and nothing to wait for. This used to call the API once per mount - and
 * ProtectedRoute mounts on every navigation, so visiting any feature-gated page cost a round
 * trip and showed the whole app shell as a skeleton while it waited.
 *
 * `loading` is kept in the shape so callers need no changes; it is always false now, because
 * the answer is already in hand by the time anything renders.
 */
export const useFeatureAccess = (): FeatureAccessState => {
  const { user } = useAuth();
  const accessibleFeatureKeys = user?.accessibleFeatureKeys ?? [];

  // A SuperAdmin is not subscribed to anything but may reach everything.
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const hasFeature = (key: string) => isSuperAdmin || accessibleFeatureKeys.includes(key);

  return {
    accessibleFeatureKeys,
    loading: false,
    hasFeature,
    hasAccounting: hasFeature('accounting'),
  };
};
